/* eslint-disable no-console -- intentional: this module manages console.log/warn/error directly to implement the tee capture */
/**
 * Console capture singleton for the in-app debug overlay.
 *
 * Responsibilities:
 *   - Tee console.log / console.warn / console.error: original methods always
 *     still fire (IAC-1, IAC-13). Buffered entries use safeSerialize so the
 *     buffer is always crash-safe.
 *   - Register window 'error' and 'unhandledrejection' listeners and buffer
 *     them as 'global-error' and 'rejection' entries.
 *   - install() / uninstall() are idempotent (IAC-3).
 *   - install(false) or calling install() when the flag is OFF is a no-op;
 *     nothing is patched (IAC-4).
 *   - uninstall() restores the EXACT original references captured at install()
 *     time via a closure record (IAC-2). This guarantees fidelity even if a
 *     third party re-wraps console between install() and uninstall().
 *   - uninstall() clears the ring buffer (IAC-2).
 *
 * Axios interceptors are intentionally NOT installed here — that is Slice 3.
 * This file only handles the console tee and window event listeners.
 */

import { httpClient } from '@/shared/api/httpClient';

import { ringBuffer } from './ringBuffer';
import { safeSerialize } from './safeSerialize';
import { store } from './store';
import type { EntryType } from './ringBuffer';

// ─── Internal closure record ─────────────────────────────────────────────────

/**
 * Captures the original console methods, event listeners, and Axios interceptor
 * IDs so uninstall() can restore exactly what was in place at install() time.
 */
interface ClosureRecord {
  log: typeof console.log;
  warn: typeof console.warn;
  error: typeof console.error;
  onError: (ev: ErrorEvent) => void;
  onRejection: (ev: PromiseRejectionEvent) => void;
  /** ID returned by httpClient.interceptors.request.use() — used for eject on uninstall. */
  axiosReqId: number;
  /** ID returned by httpClient.interceptors.response.use() — used for eject on uninstall. */
  axiosResId: number;
}

// ─── Module-level state ───────────────────────────────────────────────────────

let _installed = false;
let _record: ClosureRecord | null = null;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function pushEntry(type: EntryType, args: unknown[]): void {
  const payload = safeSerialize(args.length === 1 ? args[0] : args);
  ringBuffer.push({ t: Date.now(), type, payload });
  store.notify();
}

// ─── Exported singleton ───────────────────────────────────────────────────────

export const consoleCapture = {
  /**
   * Installs the console tee and window event listeners.
   *
   * @param enabled - When false (default: true), the call is a no-op. Allows
   *                  callers to pass `debugTelemetryService.getSync()` directly
   *                  without an extra conditional (IAC-4).
   */
  install(enabled = true): void {
    if (!enabled || _installed) return;

    // Capture originals at this exact moment (closure record).
    const origLog = console.log;
    const origWarn = console.warn;
    const origError = console.error;

    // Tee: call original then buffer.
    console.log = (...args: unknown[]) => {
      origLog(...args);
      pushEntry('log', args);
    };
    console.warn = (...args: unknown[]) => {
      origWarn(...args);
      pushEntry('warn', args);
    };
    console.error = (...args: unknown[]) => {
      origError(...args);
      pushEntry('error', args);
    };

    // Window error listener
    const onError = (ev: ErrorEvent): void => {
      pushEntry('global-error', [
        {
          message: ev.message,
          filename: ev.filename,
          lineno: ev.lineno,
          colno: ev.colno,
          error: ev.error,
        },
      ]);
    };

    // Unhandled promise rejection listener
    const onRejection = (ev: PromiseRejectionEvent): void => {
      pushEntry('rejection', [ev.reason]);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    // ── Axios interceptors ───────────────────────────────────────────────────
    //
    // Registered AFTER the existing auth + classifyApiError interceptors so that
    // the response chain order is:
    //   request:  [auth] → [iac-stamp] (LIFO → stamp runs before auth in reality,
    //             but only sets timing metadata so order is irrelevant)
    //   response: [classifyApiError] → [iac-record] (FIFO → we see the error last)
    //
    // The request interceptor stamps config.metadata.__iacStart so the response
    // error handler can compute the elapsed duration (IAC-9).
    // The response error handler does a best-effort extraction: when the error is
    // a raw AxiosError the full context is available; when classifyApiError already
    // transformed it, method/url/status degrade gracefully (debug tool tradeoff).

    const axiosReqId = httpClient.interceptors.request.use((config) => {
      config.metadata = { ...config.metadata, __iacStart: performance.now() };
      return config;
    });

    const axiosResId = httpClient.interceptors.response.use(
      undefined, // success pass-through — we only record errors via Axios
      (error: unknown) => {
        // Best-effort extraction from AxiosError shape.
        // If classifyApiError ran first, config is absent and we fall back to defaults.
        const rawErr = error as {
          config?: {
            method?: string;
            url?: string;
            metadata?: { __iacStart?: number };
          };
          response?: { status?: number };
        };

        const method = (rawErr.config?.method ?? 'UNKNOWN').toUpperCase();
        const url = rawErr.config?.url ?? '';
        const status = rawErr.response?.status ?? 0;
        const duration =
          rawErr.config?.metadata?.__iacStart !== undefined
            ? Math.round(performance.now() - rawErr.config.metadata.__iacStart)
            : -1;

        pushEntry('http', [{ method, url, status, duration }]);
        return Promise.reject(error);
      },
    );

    _record = {
      log: origLog,
      warn: origWarn,
      error: origError,
      onError,
      onRejection,
      axiosReqId,
      axiosResId,
    };
    _installed = true;
  },

  /**
   * Restores the exact original console methods captured at install() time,
   * removes event listeners, and clears the ring buffer.
   *
   * Idempotent: calling uninstall() multiple times or when never installed is
   * safe and does not throw (IAC-3).
   */
  uninstall(): void {
    if (!_installed || _record === null) return;

    // Restore originals from the closure record.
    console.log = _record.log;
    console.warn = _record.warn;
    console.error = _record.error;

    window.removeEventListener('error', _record.onError);
    window.removeEventListener('unhandledrejection', _record.onRejection);

    // Eject Axios interceptors using the IDs stored at install() time.
    httpClient.interceptors.request.eject(_record.axiosReqId);
    httpClient.interceptors.response.eject(_record.axiosResId);

    _record = null;
    _installed = false;

    // Clear the buffer so stale entries don't linger after teardown (IAC-2).
    ringBuffer.clear();
  },

  /** Returns true when the tee is active. */
  isInstalled(): boolean {
    return _installed;
  },
};
